import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import db from '../services/database';
import Icon from '../components/UI/Icon';
import { getBookName, getChapterCount } from '../constants/bibleConstants';
import './PlansPage.css';

// Map abbreviated book codes to OSIS codes
const BOOK_CODE_TO_OSIS = {
  'GEN': 'Gen', 'EXO': 'Exod', 'LEV': 'Lev', 'NUM': 'Num', 'DEU': 'Deut',
  'JOS': 'Josh', 'JDG': 'Judg', 'RUT': 'Ruth', '1SA': '1Sam', '2SA': '2Sam',
  '1KI': '1Kgs', '2KI': '2Kgs', '1CH': '1Chr', '2CH': '2Chr', 'EZR': 'Ezra',
  'NEH': 'Neh', 'EST': 'Esth', 'JOB': 'Job', 'PSA': 'Ps', 'PRO': 'Prov',
  'ECC': 'Eccl', 'SNG': 'Song', 'ISA': 'Isa', 'JER': 'Jer', 'LAM': 'Lam',
  'EZK': 'Ezek', 'DAN': 'Dan', 'HOS': 'Hos', 'JOL': 'Joel', 'AMO': 'Amos',
  'OBA': 'Obad', 'JON': 'Jonah', 'MIC': 'Mic', 'NAM': 'Nah', 'HAB': 'Hab',
  'ZEP': 'Zeph', 'HAG': 'Hag', 'ZEC': 'Zech', 'MAL': 'Mal',
  'MAT': 'Matt', 'MRK': 'Mark', 'LUK': 'Luke', 'JHN': 'John', 'ACT': 'Acts',
  'ROM': 'Rom', '1CO': '1Cor', '2CO': '2Cor', 'GAL': 'Gal', 'EPH': 'Eph',
  'PHP': 'Phil', 'COL': 'Col', '1TH': '1Thess', '2TH': '2Thess', '1TI': '1Tim',
  '2TI': '2Tim', 'TIT': 'Titus', 'PHM': 'Phlm', 'HEB': 'Heb', 'JAS': 'Jas',
  '1PE': '1Pet', '2PE': '2Pet', '1JN': '1John', '2JN': '2John', '3JN': '3John',
  'JUD': 'Jude', 'REV': 'Rev'
};

// Pre-built reading plans
const PRESET_PLANS = [
  {
    id: 'bible-year',
    name: 'Bible in a Year',
    description: 'Read through the entire Bible in one year',
    duration: 365,
    type: 'chronological',
    readings: generateBibleInYearReadings()
  },
  {
    id: 'gospels-30',
    name: 'Gospels in 30 Days',
    description: 'Journey through Matthew, Mark, Luke, and John',
    duration: 30,
    type: 'sequential',
    books: ['MAT', 'MRK', 'LUK', 'JHN']
  },
  {
    id: 'psalms-month',
    name: 'Psalms in a Month',
    description: 'Read 5 Psalms daily for 30 days',
    duration: 30,
    type: 'sequential',
    books: ['PSA']
  },
  {
    id: 'proverbs-month',
    name: 'Proverbs in a Month',
    description: 'One chapter of Proverbs per day',
    duration: 31,
    type: 'sequential',
    books: ['PRO']
  },
  {
    id: 'new-testament-90',
    name: 'New Testament in 90 Days',
    description: 'Complete the New Testament in three months',
    duration: 90,
    type: 'sequential',
    books: ['MAT', 'MRK', 'LUK', 'JHN', 'ACT', 'ROM', '1CO', '2CO', 'GAL', 'EPH', 'PHP', 'COL', '1TH', '2TH', '1TI', '2TI', 'TIT', 'PHM', 'HEB', 'JAS', '1PE', '2PE', '1JN', '2JN', '3JN', 'JUD', 'REV']
  }
];

// Bible books with chapter counts for milestone calculation
const BIBLE_BOOKS = [
  { name: 'Genesis', chapters: 50 },
  { name: 'Exodus', chapters: 40 },
  { name: 'Leviticus', chapters: 27 },
  { name: 'Numbers', chapters: 36 },
  { name: 'Deuteronomy', chapters: 34 },
  { name: 'Joshua', chapters: 24 },
  { name: 'Judges', chapters: 21 },
  { name: 'Ruth', chapters: 4 },
  { name: '1 Samuel', chapters: 31 },
  { name: '2 Samuel', chapters: 24 },
  { name: '1 Kings', chapters: 22 },
  { name: '2 Kings', chapters: 25 },
  { name: '1 Chronicles', chapters: 29 },
  { name: '2 Chronicles', chapters: 36 },
  { name: 'Ezra', chapters: 10 },
  { name: 'Nehemiah', chapters: 13 },
  { name: 'Esther', chapters: 10 },
  { name: 'Job', chapters: 42 },
  { name: 'Psalms', chapters: 150 },
  { name: 'Proverbs', chapters: 31 },
  { name: 'Ecclesiastes', chapters: 12 },
  { name: 'Song of Solomon', chapters: 8 },
  { name: 'Isaiah', chapters: 66 },
  { name: 'Jeremiah', chapters: 52 },
  { name: 'Lamentations', chapters: 5 },
  { name: 'Ezekiel', chapters: 48 },
  { name: 'Daniel', chapters: 12 },
  { name: 'Hosea', chapters: 14 },
  { name: 'Joel', chapters: 3 },
  { name: 'Amos', chapters: 9 },
  { name: 'Obadiah', chapters: 1 },
  { name: 'Jonah', chapters: 4 },
  { name: 'Micah', chapters: 7 },
  { name: 'Nahum', chapters: 3 },
  { name: 'Habakkuk', chapters: 3 },
  { name: 'Zephaniah', chapters: 3 },
  { name: 'Haggai', chapters: 2 },
  { name: 'Zechariah', chapters: 14 },
  { name: 'Malachi', chapters: 4 },
  { name: 'Matthew', chapters: 28 },
  { name: 'Mark', chapters: 16 },
  { name: 'Luke', chapters: 24 },
  { name: 'John', chapters: 21 },
  { name: 'Acts', chapters: 28 },
  { name: 'Romans', chapters: 16 },
  { name: '1 Corinthians', chapters: 16 },
  { name: '2 Corinthians', chapters: 13 },
  { name: 'Galatians', chapters: 6 },
  { name: 'Ephesians', chapters: 6 },
  { name: 'Philippians', chapters: 4 },
  { name: 'Colossians', chapters: 4 },
  { name: '1 Thessalonians', chapters: 5 },
  { name: '2 Thessalonians', chapters: 3 },
  { name: '1 Timothy', chapters: 6 },
  { name: '2 Timothy', chapters: 4 },
  { name: 'Titus', chapters: 3 },
  { name: 'Philemon', chapters: 1 },
  { name: 'Hebrews', chapters: 13 },
  { name: 'James', chapters: 5 },
  { name: '1 Peter', chapters: 5 },
  { name: '2 Peter', chapters: 3 },
  { name: '1 John', chapters: 5 },
  { name: '2 John', chapters: 1 },
  { name: '3 John', chapters: 1 },
  { name: 'Jude', chapters: 1 },
  { name: 'Revelation', chapters: 22 }
];

// Get milestone target (book + chapter) for a given day in a plan
function getMilestoneTarget(books, duration, dayNumber) {
  const totalChapters = books.reduce((sum, book) => sum + book.chapters, 0);
  const chaptersPerDay = totalChapters / duration;
  const targetChapter = Math.ceil(dayNumber * chaptersPerDay);
  
  let chapterCount = 0;
  for (const book of books) {
    if (chapterCount + book.chapters >= targetChapter) {
      const chapterInBook = targetChapter - chapterCount;
      return { book: book.name, chapter: Math.min(chapterInBook, book.chapters) };
    }
    chapterCount += book.chapters;
  }
  
  // Return last chapter of last book
  const lastBook = books[books.length - 1];
  return { book: lastBook.name, chapter: lastBook.chapters };
}

// Calculate milestones for a plan based on start date
// Short plans (≤31 days) get daily milestones, longer plans get weekly
function calculateMilestones(plan) {
  if (!plan.startDate) return [];

  const startDate = new Date(plan.startDate);
  const milestones = [];
  const isDaily = plan.duration <= 31;
  
  // Determine which books to use based on plan type
  let books;
  const isBibleInYear = plan.presetId === 'bible-year' || plan.name === 'Bible in a Year';
  const isNewTestament = plan.presetId === 'new-testament-90' || plan.name === 'New Testament in 90 Days';
  const isGospels = plan.presetId === 'gospels-30' || plan.name === 'Gospels in 30 Days';
  const isPsalms = plan.presetId === 'psalms-month' || plan.name === 'Psalms in a Month';
  const isProverbs = plan.presetId === 'proverbs-month' || plan.name === 'Proverbs in a Month';
  
  if (isBibleInYear) {
    books = BIBLE_BOOKS;
  } else if (isNewTestament) {
    books = BIBLE_BOOKS.slice(39); // Matthew onwards
  } else if (isGospels) {
    books = BIBLE_BOOKS.slice(39, 43); // Matthew, Mark, Luke, John
  } else if (isPsalms) {
    books = [{ name: 'Psalms', chapters: 150 }];
  } else if (isProverbs) {
    books = [{ name: 'Proverbs', chapters: 31 }];
  } else if (plan.books && plan.books.length > 0) {
    // Custom plan with book codes
    books = plan.books.map(bookCode => {
      const osisCode = BOOK_CODE_TO_OSIS[bookCode] || bookCode;
      return {
        name: getBookName(osisCode),
        chapters: getChapterCount(osisCode)
      };
    });
  } else {
    return [];
  }

  if (isDaily) {
    // Daily milestones for short plans
    for (let day = 1; day <= plan.duration; day++) {
      const milestoneDate = new Date(startDate);
      milestoneDate.setDate(milestoneDate.getDate() + day - 1);
      
      const target = getMilestoneTarget(books, plan.duration, day);
      
      milestones.push({
        period: day,
        day: day,
        date: milestoneDate,
        target: `${target.book} ${target.chapter}`,
        book: target.book,
        chapter: target.chapter,
        isDaily: true
      });
    }
  } else {
    // Weekly milestones for longer plans
    const totalWeeks = Math.ceil(plan.duration / 7);
    
    for (let week = 1; week <= totalWeeks; week++) {
      const dayNumber = Math.min(week * 7, plan.duration);
      const milestoneDate = new Date(startDate);
      milestoneDate.setDate(milestoneDate.getDate() + dayNumber - 1);
      
      const target = getMilestoneTarget(books, plan.duration, dayNumber);
      
      milestones.push({
        period: week,
        week,
        day: dayNumber,
        date: milestoneDate,
        target: `${target.book} ${target.chapter}`,
        book: target.book,
        chapter: target.chapter,
        isDaily: false
      });
    }
  }

  return milestones;
}

function generateBibleInYearReadings() {
  // Full Bible reading plan with verse-based distribution for balanced daily load
  const bibleBooks = [
    { id: 'Gen', name: 'Genesis', chapters: [
      31, 25, 24, 26, 32, 22, 24, 22, 29, 32, 32, 20, 18, 24, 21, 16, 27, 33, 38, 18, 34, 24, 20, 67, 34, 35, 46, 22, 35, 43, 55, 32, 20, 31, 29, 43, 36, 30, 23, 23, 57, 38, 34, 34, 28, 34, 31, 22, 33, 26
    ]},
    { id: 'Exo', name: 'Exodus', chapters: [
      22, 25, 22, 31, 23, 30, 25, 32, 35, 29, 10, 51, 22, 31, 27, 36, 16, 27, 25, 26, 36, 31, 33, 18, 40, 37, 21, 43, 46, 38, 18, 35, 23, 35, 35, 38, 29, 31, 43, 38
    ]},
    { id: 'Lev', name: 'Leviticus', chapters: [
      17, 16, 17, 35, 19, 30, 38, 36, 24, 20, 47, 8, 59, 57, 33, 34, 16, 30, 37, 27, 24, 33, 44, 23, 55, 46, 34
    ]},
    { id: 'Num', name: 'Numbers', chapters: [
      54, 34, 51, 49, 31, 27, 89, 26, 23, 36, 35, 16, 33, 45, 41, 50, 13, 32, 22, 29, 35, 41, 30, 25, 18, 65, 23, 31, 40, 16, 54, 42, 56, 29, 34, 13
    ]},
    { id: 'Deu', name: 'Deuteronomy', chapters: [
      46, 37, 29, 49, 33, 25, 26, 20, 29, 22, 32, 32, 18, 29, 23, 22, 20, 22, 21, 20, 23, 30, 25, 22, 19, 19, 26, 68, 29, 20, 30, 52, 29, 12
    ]},
    { id: 'Jos', name: 'Joshua', chapters: [
      18, 24, 17, 24, 15, 27, 26, 35, 27, 43, 23, 24, 33, 15, 63, 10, 18, 28, 51, 9, 45, 34, 16, 33
    ]},
    { id: 'Jdg', name: 'Judges', chapters: [
      36, 23, 31, 24, 31, 40, 25, 35, 57, 18, 40, 15, 25, 20, 20, 31, 13, 31, 30, 48, 25
    ]},
    { id: 'Rut', name: 'Ruth', chapters: [22, 23, 18, 22]},
    { id: '1Sa', name: '1 Samuel', chapters: [
      28, 36, 21, 22, 12, 21, 17, 22, 27, 27, 15, 25, 23, 52, 35, 23, 58, 30, 24, 42, 15, 23, 29, 22, 44, 25, 12, 25, 11, 31, 13
    ]},
    { id: '2Sa', name: '2 Samuel', chapters: [
      27, 32, 39, 12, 25, 23, 29, 18, 13, 19, 27, 31, 39, 33, 37, 23, 29, 33, 43, 26, 22, 51, 39, 25
    ]},
    { id: '1Ki', name: '1 Kings', chapters: [
      53, 46, 28, 34, 18, 38, 51, 66, 28, 29, 43, 33, 34, 31, 34, 34, 24, 46, 21, 43, 29, 53
    ]},
    { id: '2Ki', name: '2 Kings', chapters: [
      18, 25, 27, 44, 27, 33, 20, 29, 37, 36, 21, 21, 25, 29, 38, 20, 41, 37, 37, 21, 26, 20, 37, 20, 30
    ]},
    { id: '1Ch', name: '1 Chronicles', chapters: [
      54, 55, 24, 43, 26, 81, 40, 40, 44, 14, 47, 40, 14, 17, 29, 43, 27, 17, 19, 8, 30, 19, 32, 31, 31, 32, 34, 21, 30
    ]},
    { id: '2Ch', name: '2 Chronicles', chapters: [
      17, 18, 17, 22, 14, 42, 22, 18, 31, 19, 23, 16, 22, 15, 19, 14, 19, 34, 11, 37, 20, 12, 21, 27, 28, 23, 11, 27, 55, 11, 23, 23, 26, 8, 22, 58
    ]},
    { id: 'Ezr', name: 'Ezra', chapters: [11, 70, 13, 24, 17, 22, 28, 36, 15, 44]},
    { id: 'Neh', name: 'Nehemiah', chapters: [11, 20, 32, 23, 19, 19, 73, 18, 38, 39, 36, 47, 31]},
    { id: 'Est', name: 'Esther', chapters: [22, 23, 15, 17, 14, 14, 10, 17, 32, 3]},
    { id: 'Job', name: 'Job', chapters: [
      22, 13, 26, 21, 27, 30, 21, 22, 35, 22, 20, 25, 28, 22, 35, 22, 16, 21, 29, 29, 34, 30, 17, 25, 6, 14, 23, 28, 25, 31, 40, 22, 33, 37, 16, 33, 24, 41, 30, 24, 34, 17
    ]},
    { id: 'Psa', name: 'Psalms', chapters: [
      6, 12, 8, 8, 12, 10, 17, 9, 20, 18, 7, 8, 6, 7, 5, 11, 15, 50, 14, 9, 13, 31, 6, 10, 22, 12, 14, 9, 11, 12, 24, 11, 22, 22, 28, 12, 40, 22, 13, 17, 13, 11, 5, 26, 17, 11, 9, 14, 20, 23, 19, 9, 6, 7, 23, 13, 11, 11, 17, 12, 8, 12, 11, 10, 13, 20, 7, 35, 36, 5, 24, 20, 28, 23, 10, 12, 20, 72, 13, 19, 16, 8, 18, 12, 13, 17, 7, 18, 52, 17, 16, 15, 5, 23, 11, 13, 12, 9, 9, 5, 8, 28, 22, 35, 45, 48, 43, 13, 31, 7, 10, 10, 9, 8, 18, 19, 2, 29, 176, 7, 8, 9, 4, 8, 5, 6, 5, 6, 8, 8, 3, 18, 3, 3, 21, 26, 9, 8, 24, 13, 10, 7, 12, 15, 21, 10, 20, 14, 9, 6
    ]},
    { id: 'Pro', name: 'Proverbs', chapters: [
      33, 22, 35, 27, 23, 35, 27, 36, 18, 32, 31, 28, 25, 35, 33, 33, 28, 24, 29, 30, 31, 29, 35, 34, 28, 28, 27, 28, 27, 33, 31
    ]},
    { id: 'Ecc', name: 'Ecclesiastes', chapters: [18, 26, 22, 16, 20, 12, 29, 17, 18, 20, 10, 14]},
    { id: 'Sng', name: 'Song of Solomon', chapters: [17, 17, 11, 16, 16, 13, 13, 14]},
    { id: 'Isa', name: 'Isaiah', chapters: [
      31, 22, 26, 6, 30, 13, 25, 22, 21, 34, 16, 6, 22, 32, 9, 14, 14, 7, 25, 6, 17, 25, 18, 23, 12, 21, 13, 29, 24, 33, 9, 20, 24, 17, 10, 22, 38, 22, 8, 31, 29, 25, 28, 28, 25, 13, 15, 22, 26, 11, 23, 15, 12, 17, 13, 12, 21, 14, 21, 22, 11, 12, 19, 12, 25, 24
    ]},
    { id: 'Jer', name: 'Jeremiah', chapters: [
      19, 37, 25, 31, 31, 30, 34, 22, 26, 25, 23, 17, 27, 22, 21, 21, 27, 23, 15, 18, 14, 30, 40, 10, 38, 24, 22, 17, 32, 24, 40, 44, 26, 22, 19, 32, 21, 28, 18, 16, 18, 22, 13, 30, 5, 28, 7, 47, 39, 46, 64, 34
    ]},
    { id: 'Lam', name: 'Lamentations', chapters: [22, 22, 66, 22, 22]},
    { id: 'Ezk', name: 'Ezekiel', chapters: [
      28, 10, 27, 17, 17, 14, 27, 18, 11, 22, 25, 28, 23, 23, 8, 63, 24, 32, 14, 49, 32, 31, 49, 27, 17, 21, 36, 26, 21, 26, 18, 32, 33, 31, 15, 38, 28, 23, 29, 49, 26, 20, 27, 31, 25, 24, 23, 35
    ]},
    { id: 'Dan', name: 'Daniel', chapters: [21, 49, 30, 37, 31, 28, 28, 27, 27, 21, 45, 13]},
    { id: 'Hos', name: 'Hosea', chapters: [11, 23, 5, 19, 15, 11, 16, 14, 17, 15, 12, 14, 16, 9]},
    { id: 'Jol', name: 'Joel', chapters: [20, 32, 21]},
    { id: 'Amo', name: 'Amos', chapters: [15, 16, 15, 13, 27, 14, 17, 14, 15]},
    { id: 'Oba', name: 'Obadiah', chapters: [21]},
    { id: 'Jon', name: 'Jonah', chapters: [17, 10, 10, 11]},
    { id: 'Mic', name: 'Micah', chapters: [16, 13, 12, 13, 15, 16, 20]},
    { id: 'Nam', name: 'Nahum', chapters: [15, 13, 19]},
    { id: 'Hab', name: 'Habakkuk', chapters: [17, 20, 19]},
    { id: 'Zep', name: 'Zephaniah', chapters: [18, 15, 20]},
    { id: 'Hag', name: 'Haggai', chapters: [15, 23]},
    { id: 'Zec', name: 'Zechariah', chapters: [21, 13, 10, 14, 11, 15, 14, 23, 17, 12, 17, 14, 9, 21]},
    { id: 'Mal', name: 'Malachi', chapters: [16, 17, 10, 23]},
    { id: 'Mat', name: 'Matthew', chapters: [
      25, 23, 17, 25, 48, 34, 29, 34, 38, 42, 30, 50, 58, 36, 39, 28, 27, 35, 30, 34, 46, 46, 39, 51, 46, 75, 66, 20
    ]},
    { id: 'Mrk', name: 'Mark', chapters: [
      45, 28, 35, 41, 43, 56, 37, 38, 50, 52, 33, 44, 37, 72, 47, 20
    ]},
    { id: 'Luk', name: 'Luke', chapters: [
      80, 52, 38, 44, 39, 49, 50, 56, 62, 42, 54, 59, 35, 35, 32, 31, 37, 43, 48, 47, 38, 71, 56, 53
    ]},
    { id: 'Jhn', name: 'John', chapters: [
      51, 25, 36, 54, 47, 71, 53, 59, 41, 42, 57, 50, 38, 31, 27, 33, 26, 40, 42, 31, 25
    ]},
    { id: 'Act', name: 'Acts', chapters: [
      26, 47, 26, 37, 42, 15, 60, 40, 43, 48, 30, 25, 52, 28, 41, 40, 34, 28, 41, 38, 40, 30, 35, 27, 27, 32, 44, 31
    ]},
    { id: 'Rom', name: 'Romans', chapters: [32, 29, 31, 25, 21, 23, 25, 39, 33, 21, 36, 21, 14, 23, 33, 27]},
    { id: '1Co', name: '1 Corinthians', chapters: [31, 16, 23, 21, 13, 20, 40, 13, 27, 33, 34, 31, 13, 40, 58, 24]},
    { id: '2Co', name: '2 Corinthians', chapters: [24, 17, 18, 18, 21, 18, 16, 24, 15, 18, 33, 21, 14]},
    { id: 'Gal', name: 'Galatians', chapters: [24, 21, 29, 31, 26, 18]},
    { id: 'Eph', name: 'Ephesians', chapters: [23, 22, 21, 32, 33, 24]},
    { id: 'Php', name: 'Philippians', chapters: [30, 30, 21, 23]},
    { id: 'Col', name: 'Colossians', chapters: [29, 23, 25, 18]},
    { id: '1Th', name: '1 Thessalonians', chapters: [10, 20, 13, 18, 28]},
    { id: '2Th', name: '2 Thessalonians', chapters: [12, 17, 18]},
    { id: '1Ti', name: '1 Timothy', chapters: [20, 15, 16, 16, 25, 21]},
    { id: '2Ti', name: '2 Timothy', chapters: [18, 26, 17, 22]},
    { id: 'Tit', name: 'Titus', chapters: [16, 15, 15]},
    { id: 'Phm', name: 'Philemon', chapters: [25]},
    { id: 'Heb', name: 'Hebrews', chapters: [14, 18, 19, 16, 14, 20, 28, 13, 28, 39, 40, 29, 25]},
    { id: 'Jas', name: 'James', chapters: [27, 26, 18, 17, 20]},
    { id: '1Pe', name: '1 Peter', chapters: [25, 25, 22, 19, 14]},
    { id: '2Pe', name: '2 Peter', chapters: [21, 22, 18]},
    { id: '1Jn', name: '1 John', chapters: [10, 29, 24, 21, 21]},
    { id: '2Jn', name: '2 John', chapters: [13]},
    { id: '3Jn', name: '3 John', chapters: [15]},
    { id: 'Jud', name: 'Jude', chapters: [25]},
    { id: 'Rev', name: 'Revelation', chapters: [
      20, 29, 22, 11, 14, 17, 17, 13, 21, 11, 19, 17, 18, 20, 8, 21, 18, 24, 21, 15, 27, 21
    ]}
  ];

  // Calculate total verses in the Bible
  const totalVerses = bibleBooks.reduce((sum, book) =>
    sum + book.chapters.reduce((bookSum, chapter) => bookSum + chapter, 0), 0
  );

  const versesPerDay = totalVerses / 365;

  const readings = [];
  let currentBookIndex = 0;
  let currentChapterIndex = 0;
  let currentVerseInChapter = 1;
  let versesRead = 0;

  for (let day = 1; day <= 365; day++) {
    const dayReadings = [];
    const targetVerses = Math.ceil(day * versesPerDay) - versesRead;

    let versesToday = 0;

    while (versesToday < targetVerses && currentBookIndex < bibleBooks.length) {
      const book = bibleBooks[currentBookIndex];
      const chapterVerses = book.chapters[currentChapterIndex];

      if (!chapterVerses) {
        // Move to next book
        currentBookIndex++;
        currentChapterIndex = 0;
        currentVerseInChapter = 1;
        continue;
      }

      const startVerse = currentVerseInChapter;
      const remainingInChapter = chapterVerses - currentVerseInChapter + 1;
      const versesFromChapter = Math.min(remainingInChapter, targetVerses - versesToday);

      dayReadings.push({
        book: book.name,
        bookId: book.id,
        chapter: currentChapterIndex + 1,
        startVerse,
        endVerse: startVerse + versesFromChapter - 1
      });

      versesToday += versesFromChapter;
      currentVerseInChapter += versesFromChapter;

      if (currentVerseInChapter > chapterVerses) {
        // Move to next chapter
        currentChapterIndex++;
        currentVerseInChapter = 1;
      }
    }

    versesRead += versesToday;

    readings.push({
      day,
      readings: dayReadings,
      cumulativeVerses: versesRead
    });
  }

  return readings;
}

function PlansPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [view, setView] = useState('list'); // 'list', 'create', 'detail'
  const [customPlan, setCustomPlan] = useState({
    name: '',
    description: '',
    startDate: new Date().toISOString().split('T')[0],
    duration: 30,
    books: [],
    adaptivePacing: true
  });
  const [isLoading, setIsLoading] = useState(true);

  // Load reading plans
  useEffect(() => {
    async function loadPlans() {
      try {
        const savedPlans = await db.readingPlans.toArray();
        setPlans(savedPlans);
        const active = savedPlans.find(p => p.isActive);
        if (active) setActivePlan(active);
      } catch (err) {
        console.error('Failed to load reading plans:', err);
      } finally {
        setIsLoading(false);
      }
    }
    loadPlans();
  }, []);

  // Start a preset plan
  const handleStartPreset = useCallback(async (preset) => {
    // Don't save the large readings array to DB - we regenerate it on the fly
    const { readings, ...presetWithoutReadings } = preset;
    
    const plan = {
      ...presetWithoutReadings,
      presetId: preset.id, // Keep track of original preset ID
      startDate: new Date().toISOString(),
      isActive: true,
      completedDays: [],
      currentDay: 1,
      adaptivePacing: true,
      createdAt: new Date().toISOString()
    };

    try {
      // Deactivate other plans
      await db.readingPlans.where('isActive').equals(1).modify({ isActive: false });
      
      const id = await db.readingPlans.add(plan);
      const newPlan = { ...plan, id };
      setPlans(prev => [...prev.map(p => ({ ...p, isActive: false })), newPlan]);
      setActivePlan(newPlan);
      setView('detail');
    } catch (err) {
      console.error('Failed to start plan:', err);
    }
  }, []);

  // Create custom plan
  const handleCreateCustom = useCallback(async () => {
    if (!customPlan.name || customPlan.books.length === 0) return;

    const plan = {
      ...customPlan,
      type: 'custom',
      startDate: new Date().toISOString(),
      isActive: true,
      completedDays: [],
      currentDay: 1,
      createdAt: new Date().toISOString()
    };

    try {
      await db.readingPlans.where('isActive').equals(1).modify({ isActive: false });
      
      const id = await db.readingPlans.add(plan);
      const newPlan = { ...plan, id };
      setPlans(prev => [...prev.map(p => ({ ...p, isActive: false })), newPlan]);
      setActivePlan(newPlan);
      setView('detail');
      setCustomPlan({
        name: '',
        description: '',
        startDate: new Date().toISOString().split('T')[0],
        duration: 30,
        books: [],
        adaptivePacing: true
      });
    } catch (err) {
      console.error('Failed to create custom plan:', err);
    }
  }, [customPlan]);

  // Toggle period (day or week) completion
  const handleTogglePeriod = useCallback(async (period, isDaily) => {
    if (!activePlan) return;

    const key = isDaily ? 'completedDays' : 'completedWeeks';
    const completedPeriods = activePlan[key] || [];
    const newCompletedPeriods = completedPeriods.includes(period)
      ? completedPeriods.filter(p => p !== period)
      : [...completedPeriods, period];

    const updates = {
      [key]: newCompletedPeriods
    };

    try {
      await db.readingPlans.update(activePlan.id, updates);
      setActivePlan(prev => ({ ...prev, ...updates }));
      setPlans(prev => prev.map(p => 
        p.id === activePlan.id ? { ...p, ...updates } : p
      ));
    } catch (err) {
      console.error('Failed to update progress:', err);
    }
  }, [activePlan]);

  // Delete plan
  const handleDeletePlan = useCallback(async (id) => {
    if (!window.confirm('Are you sure you want to delete this reading plan?')) return;

    try {
      await db.readingPlans.delete(id);
      setPlans(prev => prev.filter(p => p.id !== id));
      if (activePlan?.id === id) {
        setActivePlan(null);
        setView('list');
      }
    } catch (err) {
      console.error('Failed to delete plan:', err);
    }
  }, [activePlan]);

  // Calculate progress based on completed periods (days for short plans, weeks for long)
  const getProgress = (plan) => {
    const isDaily = plan.duration <= 31;
    if (isDaily) {
      const completedDays = plan.completedDays?.length || 0;
      return Math.round((completedDays / plan.duration) * 100);
    } else {
      const completedWeeks = plan.completedWeeks?.length || 0;
      const totalWeeks = Math.ceil(plan.duration / 7);
      return Math.round((completedWeeks / totalWeeks) * 100);
    }
  };

  // Book options for custom plan
  const BOOK_OPTIONS = [
    { group: 'Old Testament', books: ['GEN', 'EXO', 'LEV', 'NUM', 'DEU', 'JOS', 'JDG', 'RUT', '1SA', '2SA', '1KI', '2KI', '1CH', '2CH', 'EZR', 'NEH', 'EST', 'JOB', 'PSA', 'PRO', 'ECC', 'SNG', 'ISA', 'JER', 'LAM', 'EZK', 'DAN', 'HOS', 'JOL', 'AMO', 'OBA', 'JON', 'MIC', 'NAM', 'HAB', 'ZEP', 'HAG', 'ZEC', 'MAL'] },
    { group: 'New Testament', books: ['MAT', 'MRK', 'LUK', 'JHN', 'ACT', 'ROM', '1CO', '2CO', 'GAL', 'EPH', 'PHP', 'COL', '1TH', '2TH', '1TI', '2TI', 'TIT', 'PHM', 'HEB', 'JAS', '1PE', '2PE', '1JN', '2JN', '3JN', 'JUD', 'REV'] }
  ];

  return (
    <div className="plans-page page">
      <header className="page-header">
        <div className="header-content">
          <h1 className="page-title">Reading Plans</h1>
          {view !== 'list' && (
            <button className="btn btn-ghost" onClick={() => setView('list')}>
              <Icon name="chevronLeft" size={18} />
              Back
            </button>
          )}
        </div>
        {view === 'list' && (
          <button className="btn btn-primary" onClick={() => setView('create')}>
            <Icon name="plus" size={18} />
            Create Plan
          </button>
        )}
      </header>

      <div className="page-content">
        {isLoading ? (
          <div className="loading-state">Loading plans...</div>
        ) : view === 'list' ? (
          <>
            {/* Active Plan */}
            {activePlan && (
              <section className="section">
                <h2 className="section-title">Current Plan</h2>
                <div 
                  className="plan-card active"
                  onClick={() => setView('detail')}
                >
                  <div className="plan-card-header">
                    <h3>{activePlan.name}</h3>
                    <span className="badge">Active</span>
                  </div>
                  <p className="plan-description">{activePlan.description}</p>
                  <div className="plan-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ width: `${getProgress(activePlan)}%` }}
                      />
                    </div>
                    <span className="progress-text">
                      {getProgress(activePlan)}% complete
                    </span>
                  </div>
                  <div className="plan-meta">
                    {activePlan.currentPosition ? (
                      <span>Currently at: {activePlan.currentPosition.bookName}, Chapter {activePlan.currentPosition.chapter}</span>
                    ) : (
                      <span>Day {activePlan.currentDay || 1} of {activePlan.duration}</span>
                    )}
                    <span>{activePlan.completedDays?.length || activePlan.completedWeeks?.length || 0} {activePlan.duration <= 31 ? 'days' : 'weeks'} completed</span>
                  </div>
                  <button
                    className="btn btn-primary btn-sm"
                    style={{ marginTop: 8 }}
                    onClick={e => {
                      e.stopPropagation();
                      // Use saved position if available, otherwise use milestone
                      if (activePlan.currentPosition) {
                        // Go to the NEXT chapter after where they left off
                        const { bookId, bookName, chapter } = activePlan.currentPosition;
                        const bookChapterCount = getChapterCount(bookId) || 50;
                        
                        if (chapter < bookChapterCount) {
                          // Next chapter in same book
                          navigate(`/reader/${bookId}/${chapter + 1}`);
                        } else {
                          // Need to go to next book - find it in BIBLE_BOOKS
                          const bookIndex = BIBLE_BOOKS.findIndex(b => b.name === bookName);
                          if (bookIndex >= 0 && bookIndex < BIBLE_BOOKS.length - 1) {
                            const nextBook = BIBLE_BOOKS[bookIndex + 1];
                            // Find OSIS code for next book
                            const nextOsisCode = Object.entries(BOOK_CODE_TO_OSIS).find(
                              ([abbr, osis]) => getBookName(osis) === nextBook.name
                            )?.[1] || nextBook.name;
                            navigate(`/reader/${nextOsisCode}/1`);
                          } else {
                            // At end of Bible, just go to same spot
                            navigate(`/reader/${bookId}/${chapter}`);
                          }
                        }
                      } else {
                        // Fall back to milestone-based navigation
                        const milestones = calculateMilestones(activePlan);
                        const current = milestones[(activePlan.currentDay || 1) - 1];
                        if (current) {
                          const [bookName, chapter] = current.target.split(' ');
                          const osisCode = Object.values(BOOK_CODE_TO_OSIS).find(
                            code => getBookName(code) === bookName
                          ) || bookName;
                          navigate(`/reader/${osisCode}/${chapter}`);
                        }
                      }
                    }}
                  >
                    <Icon name="chevronRight" size={16} /> Resume Plan
                  </button>
                </div>
              </section>
            )}

            {/* Your Plans */}
            {plans.filter(p => !p.isActive).length > 0 && (
              <section className="section">
                <h2 className="section-title">Your Plans</h2>
                <div className="plans-grid">
                  {plans.filter(p => !p.isActive).map(plan => (
                    <div key={plan.id} className="plan-card">
                      <div className="plan-card-header">
                        <h3>{plan.name}</h3>
                        {plan.completedDays?.length === plan.duration && (
                          <span className="badge success">Completed</span>
                        )}
                      </div>
                      <p className="plan-description">{plan.description}</p>
                      <div className="plan-progress">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill"
                            style={{ width: `${getProgress(plan)}%` }}
                          />
                        </div>
                        <span className="progress-text">
                          {getProgress(plan)}% complete
                        </span>
                      </div>
                      <div className="plan-actions">
                        <button 
                          className="btn btn-primary btn-sm"
                          onClick={async () => {
                            await db.readingPlans.where('isActive').equals(1).modify({ isActive: false });
                            await db.readingPlans.update(plan.id, { isActive: true });
                            setPlans(prev => prev.map(p => ({ ...p, isActive: p.id === plan.id })));
                            setActivePlan(plan);
                          }}
                        >
                          Resume
                        </button>
                        <button 
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleDeletePlan(plan.id)}
                        >
                          <Icon name="trash" size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Preset Plans */}
            <section className="section">
              <h2 className="section-title">Start a New Plan</h2>
              <div className="plans-grid">
                {PRESET_PLANS.map(preset => (
                  <div key={preset.id} className="plan-card preset">
                    <div className="plan-card-header">
                      <h3>{preset.name}</h3>
                      <span className="duration">{preset.duration} days</span>
                    </div>
                    <p className="plan-description">{preset.description}</p>
                    <button 
                      className="btn btn-primary"
                      onClick={() => handleStartPreset(preset)}
                    >
                      Start Plan
                    </button>
                  </div>
                ))}
              </div>
            </section>
          </>
        ) : view === 'create' ? (
          <div className="create-plan">
            <h2 className="section-title">Create Custom Plan</h2>
            
            <div className="form-group">
              <label className="form-label">Plan Name</label>
              <input
                type="text"
                className="form-input"
                value={customPlan.name}
                onChange={(e) => setCustomPlan(prev => ({ ...prev, name: e.target.value }))}
                placeholder="My Reading Plan"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-input"
                value={customPlan.description}
                onChange={(e) => setCustomPlan(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your reading plan..."
                rows={3}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Duration (days)</label>
                <input
                  type="number"
                  className="form-input"
                  value={customPlan.duration}
                  onChange={(e) => setCustomPlan(prev => ({ 
                    ...prev, 
                    duration: parseInt(e.target.value) || 30 
                  }))}
                  min={1}
                  max={365}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={customPlan.startDate}
                  onChange={(e) => setCustomPlan(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Select Books to Read</label>
              {BOOK_OPTIONS.map(({ group, books }) => (
                <div key={group} className="book-group">
                  <div className="book-group-header">
                    <span>{group}</span>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        const allSelected = books.every(b => customPlan.books.includes(b));
                        setCustomPlan(prev => ({
                          ...prev,
                          books: allSelected 
                            ? prev.books.filter(b => !books.includes(b))
                            : [...new Set([...prev.books, ...books])]
                        }));
                      }}
                    >
                      {books.every(b => customPlan.books.includes(b)) ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="book-checkboxes">
                    {books.map(book => (
                      <label key={book} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={customPlan.books.includes(book)}
                          onChange={(e) => {
                            setCustomPlan(prev => ({
                              ...prev,
                              books: e.target.checked
                                ? [...prev.books, book]
                                : prev.books.filter(b => b !== book)
                            }));
                          }}
                        />
                        {book}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={customPlan.adaptivePacing}
                  onChange={(e) => setCustomPlan(prev => ({ 
                    ...prev, 
                    adaptivePacing: e.target.checked 
                  }))}
                />
                <span>
                  <strong>Adaptive Pacing</strong>
                  <small>Adjust reading load based on your progress</small>
                </span>
              </label>
            </div>

            <div className="form-actions">
              <button 
                className="btn btn-ghost"
                onClick={() => setView('list')}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleCreateCustom}
                disabled={!customPlan.name || customPlan.books.length === 0}
              >
                Create Plan
              </button>
            </div>
          </div>
        ) : view === 'detail' && activePlan ? (
          <div className="plan-detail">
            <div className="plan-header">
              <div>
                <h2>{activePlan.name}</h2>
                <p className="plan-description">{activePlan.description}</p>
              </div>
              <div className="plan-stats">
                <div className="stat">
                  <span className="stat-value">
                    {activePlan.duration <= 31 
                      ? (activePlan.completedDays?.length || 0)
                      : (activePlan.completedWeeks?.length || 0)
                    }
                  </span>
                  <span className="stat-label">
                    {activePlan.duration <= 31 ? 'Days' : 'Weeks'} Done
                  </span>
                </div>
                <div className="stat">
                  <span className="stat-value">
                    {activePlan.duration <= 31 
                      ? activePlan.duration - (activePlan.completedDays?.length || 0)
                      : Math.ceil(activePlan.duration / 7) - (activePlan.completedWeeks?.length || 0)
                    }
                  </span>
                  <span className="stat-label">
                    {activePlan.duration <= 31 ? 'Days' : 'Weeks'} Left
                  </span>
                </div>
                <div className="stat">
                  <span className="stat-value">{getProgress(activePlan)}%</span>
                  <span className="stat-label">Progress</span>
                </div>
              </div>
            </div>

            <div className="progress-section">
              <div className="progress-bar large">
                <div 
                  className="progress-fill"
                  style={{ width: `${getProgress(activePlan)}%` }}
                />
              </div>
            </div>

            {/* Milestones */}
            {(activePlan.presetId || activePlan.books) && (
              <div className="milestones-section">
                <h3 className="milestones-title">
                  <Icon name="flag" size={18} />
                  {activePlan.duration <= 31 ? 'Daily Reading' : 'Weekly Milestones'}
                </h3>
                <p className="milestones-subtitle">
                  {activePlan.duration <= 31 
                    ? 'Check off each day as you complete your reading'
                    : 'Check off each week as you complete your reading'
                  }
                </p>
                <div className={`milestones-list ${activePlan.duration <= 31 ? 'daily-list' : ''}`}>
                  {calculateMilestones(activePlan).map((milestone) => {
                    const isDaily = milestone.isDaily;
                    const completedPeriods = isDaily 
                      ? (activePlan.completedDays || [])
                      : (activePlan.completedWeeks || []);
                    const isCompleted = completedPeriods.includes(milestone.period);
                    const isPast = new Date() > milestone.date;
                    
                    // Calculate current period
                    const daysSinceStart = Math.floor((new Date() - new Date(activePlan.startDate)) / (24 * 60 * 60 * 1000));
                    const currentPeriod = isDaily 
                      ? daysSinceStart + 1
                      : Math.ceil((daysSinceStart + 1) / 7);
                    const isCurrent = milestone.period === currentPeriod;
                    
                    const formattedDate = milestone.date.toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    });
                    
                    return (
                      <div 
                        key={milestone.period}
                        className={`milestone-card ${isCompleted ? 'completed' : ''} ${isPast && !isCompleted ? 'behind' : ''} ${isCurrent ? 'current' : ''}`}
                      >
                        <button
                          className="milestone-checkbox-btn"
                          onClick={() => handleTogglePeriod(milestone.period, isDaily)}
                          tabIndex={0}
                          aria-label={isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
                        >
                          <div className="milestone-checkbox">
                            {isCompleted ? (
                              <Icon name="check" size={18} />
                            ) : (
                              <div className="checkbox-empty" />
                            )}
                          </div>
                        </button>
                        <button
                          className="milestone-content-btn"
                          onClick={() => {
                            // Navigate to the correct book/chapter
                            const [bookName, chapter] = milestone.target.split(' ');
                            // Find OSIS code for book
                            const osisCode = Object.values(BOOK_CODE_TO_OSIS).find(
                              code => getBookName(code) === bookName
                            ) || bookName;
                            // Navigate to reader
                            navigate(`/reader/${osisCode}/${chapter}`);
                          }}
                          tabIndex={0}
                          aria-label={`Go to ${milestone.target}`}
                        >
                          <div className="milestone-content">
                            <div className="milestone-week">
                              <span>{isDaily ? `Day ${milestone.period}` : `Week ${milestone.period}`}</span>
                              {isCurrent && !isCompleted && (
                                <span className="milestone-badge current-badge">Current</span>
                              )}
                            </div>
                            <div className="milestone-target">
                              <span className="milestone-description">
                                Read through {milestone.target} by {formattedDate}
                              </span>
                            </div>
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="plan-actions-footer">
              <button 
                className="btn btn-ghost"
                onClick={() => handleDeletePlan(activePlan.id)}
              >
                <Icon name="trash" size={18} />
                Delete Plan
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default PlansPage;
